import type { RestaurantMembership } from './types';

/** Role controls the action; this scope controls which departmental records it may target. */
export type DepartmentScopedAccess = Pick<RestaurantMembership, 'role' | 'departmentAccess' | 'departmentIds' | 'departmentId'>;

/** null means all departments. An empty list deliberately grants no departmental access. */
export function departmentScope(member: DepartmentScopedAccess): string[] | null {
  if (member.role === 'OWNER') return null;
  if (member.departmentAccess === 'ALL') return null;
  const validIds = (ids: unknown): string[] => {
    if (!Array.isArray(ids) || ids.some((id) => typeof id !== 'string' || !id.trim())) return [];
    return [...new Set(ids.map((id: string) => id.trim()))];
  };
  if (member.departmentAccess === 'SELECTED') return validIds(member.departmentIds);
  // Unknown persisted access modes fail closed instead of silently granting the whole restaurant.
  if (member.departmentAccess != null) return [];
  if (member.departmentIds != null) return validIds(member.departmentIds);
  if (member.departmentId != null) return typeof member.departmentId === 'string' && member.departmentId.trim() ? [member.departmentId.trim()] : [];
  return null;
}

export function canAccessDepartment(member: DepartmentScopedAccess, departmentId: string | null | undefined): boolean {
  const scope = departmentScope(member);
  return scope === null || (typeof departmentId === 'string' && scope.includes(departmentId));
}

export function isDepartmentRestricted(member: DepartmentScopedAccess): boolean {
  return departmentScope(member) !== null;
}

/** Whether a target's entire departmental scope fits within the actor's scope. Not a role check. */
export function scopeWithin(actor: DepartmentScopedAccess, target: DepartmentScopedAccess): boolean {
  const allowed = departmentScope(actor), requested = departmentScope(target);
  if (allowed === null) return true;
  return requested !== null && requested.length > 0 && requested.every((id) => allowed.includes(id));
}
